import { memo, useMemo } from 'react';
import { Skeleton, Stack } from '@mui/material';
import Table, { ColumnOption } from 'domains/core/components/Table';
import { AccessCentreRole } from 'domains/accessCentre/types';
import { formatTimestamp } from 'domains/accessCentre/util';
import RolesTableOptionsCell from './RolesTableOptionsCell';

type Props = {
    roles: AccessCentreRole[];
    isLoading?: boolean;
    isRefetching?: boolean;
    refetch?: () => void;
};

const RolesTable = ({ roles, isLoading, isRefetching, refetch }: Props) => {
    const data: object[] = useMemo(() => [...roles], [roles]);

    const columns: ColumnOption[] = useMemo(
        () => [
            { Header: 'Name', accessor: 'name', align: 'left', disableSortBy: true },
            {
                Header: 'Number of users',
                accessor: 'user_count',
                align: 'left',
                disableSortBy: true,
            },
            {
                Header: 'Updated at',
                accessor: 'last_modified_timestamp',
                disableSortBy: true,
                Cell: (row: any) => formatTimestamp(row.row.original.last_modified_timestamp),
            },
            {
                Header: 'Updated by',
                accessor: 'updated_by',
                align: 'left',
                disableSortBy: true,
                Cell: (row: any) => row.cell.value || '—',
            },
            {
                Header: '',
                accessor: 'id',
                align: 'right',
                disableSortBy: true,
                Cell: (val: any) => <RolesTableOptionsCell role={val.row.original} />,
            },
        ],
        []
    );

    if (isLoading) {
        return (
            <Stack
                spacing={1}
                sx={{ pt: 2 }}
                data-testid="access-center-roles-table-loading"
                role="progressbar"
                aria-label="Loading roles"
            >
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={48} />
                ))}
            </Stack>
        );
    }

    return (
        <Stack data-testid="access-center-roles-table" role="table" aria-label="Roles table">
            <Table columns={columns} data={data} isRefetching={isRefetching} refetch={refetch} stickyHeader />
        </Stack>
    );
};

export default memo(RolesTable);
